import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { Icon, type IconName } from './Icon'

export interface SelectOption<T extends string | number> {
  value: T
  label: string
  /** Число справа от пункта: «Без фото · 8». Ноль не показываем. */
  count?: number
}

export interface SelectProps<T extends string | number> {
  /** Имя для читалки и заголовок шторки на телефоне. */
  label: string
  value: T
  options: SelectOption<T>[]
  onChange: (value: T) => void
  /** Подпись внутри кнопки, когда по значению не понять, что это: «Сортировка». */
  prefix?: string
  /** Значение не по умолчанию — список сужен, это видно. */
  active?: boolean
  size?: 'sm'
  /**
   * Кнопка-иконка вместо поля: для второстепенного выбора вроде сортировки.
   * Что выбрано — в подсказке «Сортировка: По названию».
   */
  icon?: IconName
  /** Список прижат к правому краю кнопки — для кнопок у правого края экрана. */
  align?: 'end'
  /** Значок перед значением: «⇅ По названию» понятнее, чем просто «По названию». */
  lead?: IconName
  /** Только просмотр: значение видно, список не открывается. */
  disabled?: boolean
}

/** Сколько места нужно списку снизу; меньше — открываем вверх. */
const ROOM_BELOW = 280
/** Пауза, после которой набранные буквы начинают поиск заново. */
const TYPEAHEAD_MS = 600

/**
 * Выпадающий список вместо нативного <select>: тот рисует системное меню,
 * которое не подчиняется ни шрифту, ни цветам, ни плотности админки.
 *
 * Поведение — WAI-ARIA «select-only combobox»: фокус остаётся на кнопке,
 * активный пункт — через aria-activedescendant. Стрелки, Home/End, Enter и
 * пробел, Escape, набор первых букв. Мало места снизу — список открывается
 * вверх; на телефоне он становится шторкой снизу экрана.
 */
export function Select<T extends string | number>({
  label,
  value,
  options,
  onChange,
  prefix,
  active = false,
  size,
  icon,
  align,
  lead,
  disabled = false,
}: SelectProps<T>) {
  const id = useId()
  const listId = `${id}-list`
  const [open, setOpen] = useState(false)
  // Где рисовать список: он живёт в портале поверх страницы, иначе рамка
  // прокрутки таблицы обрезала бы его у выпадающих списков в строках.
  const [place, setPlace] = useState<{ up: boolean; style: CSSProperties }>({
    up: false,
    style: {},
  })
  const [cursor, setCursor] = useState(0)
  const typed = useRef('')
  const typedTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  )
  const valueLabel = options[selectedIndex]?.label ?? ''
  const last = options.length - 1

  useEffect(() => {
    if (!open) return
    const inside = `[data-select="${id}"] > .ui-select, [data-select-pop="${id}"]`
    const onDown = (event: PointerEvent) => {
      // Внутри — только кнопка и сам список: затемнение под шторкой тоже «мимо».
      if (!(event.target as Element).closest(inside)) setOpen(false)
    }
    // Список прибит к месту кнопки: страница уехала — закрываем, а не
    // оставляем висеть в стороне. Прокрутка самого списка не в счёт.
    const onScroll = (event: Event) => {
      if (!(event.target instanceof Element && event.target.closest(inside))) setOpen(false)
    }
    const onResize = () => {
      setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open, id])

  useEffect(
    () => () => {
      clearTimeout(typedTimer.current)
    },
    [],
  )

  function openAt(index: number, trigger: HTMLElement) {
    const rect = trigger.getBoundingClientRect()
    const below = window.innerHeight - rect.bottom
    const up = below < ROOM_BELOW && rect.top > below
    setPlace({
      up,
      style: {
        minWidth: rect.width,
        ...(up ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
        ...(align === 'end'
          ? { right: document.documentElement.clientWidth - rect.right }
          : { left: rect.left }),
      },
    })
    setCursor(index)
    setOpen(true)
  }

  function commit(index: number) {
    const option = options[index]
    if (option) onChange(option.value)
    setOpen(false)
  }

  function typeahead(char: string, trigger: HTMLElement) {
    clearTimeout(typedTimer.current)
    typed.current += char.toLowerCase()
    typedTimer.current = setTimeout(() => {
      typed.current = ''
    }, TYPEAHEAD_MS)
    const from = open ? cursor : selectedIndex
    // Одна и та же буква листает пункты на неё по кругу.
    const repeat = typed.current.length > 1 && new Set(typed.current).size === 1
    const needle = repeat ? char.toLowerCase() : typed.current
    const order = [...options.slice(from + 1), ...options.slice(0, from + 1)]
    const match = order.find((o) => o.label.toLowerCase().startsWith(needle))
    if (match) openAt(options.indexOf(match), trigger)
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const trigger = event.currentTarget
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (open) setCursor(Math.min(cursor + 1, last))
        else openAt(selectedIndex, trigger)
        return
      case 'ArrowUp':
        event.preventDefault()
        if (open) setCursor(Math.max(cursor - 1, 0))
        else openAt(selectedIndex, trigger)
        return
      case 'Home':
        event.preventDefault()
        openAt(0, trigger)
        return
      case 'End':
        event.preventDefault()
        openAt(last, trigger)
        return
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (open) commit(cursor)
        else openAt(selectedIndex, trigger)
        return
      case 'Escape':
        if (open) {
          event.preventDefault()
          setOpen(false)
        }
        return
      case 'Tab':
        setOpen(false)
        return
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          typeahead(event.key, trigger)
        }
    }
  }

  function onClick(event: MouseEvent<HTMLButtonElement>) {
    if (open) setOpen(false)
    else openAt(selectedIndex, event.currentTarget)
  }

  return (
    <div className="ui-selectbox" data-select={id} data-size={size}>
      <button
        type="button"
        role="combobox"
        className={icon ? 'ui-iconbtn ui-select-icon' : 'ui-select'}
        data-tip={icon && !open ? `${label}: ${valueLabel}` : undefined}
        data-tip-pos={icon ? 'bottom' : undefined}
        data-tip-align={icon ? align : undefined}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? `${id}-${cursor}` : undefined}
        data-active={active || undefined}
        disabled={disabled}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        {icon ? (
          <Icon name={icon} />
        ) : (
          <>
            {lead ? <Icon name={lead} size={16} /> : null}
            {prefix ? <span className="prefix">{prefix}:</span> : null}
            <span className="value">{valueLabel}</span>
          </>
        )}
      </button>
      {open
        ? createPortal(
            <>
              {/* На телефоне — затемнение под шторкой; нажатие по нему закрывает список. */}
              <div className="ui-select-scrim" aria-hidden="true" />
              <div
                className="ui-select-pop"
                data-select-pop={id}
                data-up={place.up || undefined}
                data-align={align}
                data-titled={icon ? true : undefined}
                style={place.style}
              >
                <div className="ui-select-title" aria-hidden="true">
                  {label}
                </div>
                <ul
                  id={listId}
                  role="listbox"
                  aria-label={label}
                  // Нажатие по пункту не должно уводить фокус с кнопки.
                  onMouseDown={(event) => {
                    event.preventDefault()
                  }}
                >
                  {options.map((option, index) => (
                    // Клавиатуру ведёт кнопка через aria-activedescendant (WAI-ARIA
                    // select-only combobox): у пунктов нет своего фокуса, им нужен
                    // только щелчок мышью или касание.
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                    <li
                      key={option.value}
                      id={`${id}-${index}`}
                      role="option"
                      aria-selected={index === selectedIndex}
                      data-cursor={index === cursor || undefined}
                      ref={
                        index === cursor
                          ? (node) => {
                              node?.scrollIntoView({ block: 'nearest' })
                            }
                          : undefined
                      }
                      onClick={() => {
                        commit(index)
                      }}
                      onMouseMove={() => {
                        setCursor(index)
                      }}
                    >
                      <span className="check" aria-hidden="true">
                        {index === selectedIndex ? <Icon name="i-check" size={16} /> : null}
                      </span>
                      <span className="text">{option.label}</span>
                      {option.count ? <span className="ui-count">{option.count}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  )
}
